import {Injectable} from '@angular/core';
import {LatestVehicleEntry} from '../../../../core/component/latest-vehicles-info/model/latest-vehicle-entry';
import {Observable} from 'rxjs';
import {PlaceSource} from '../../../../core/component/places/model/place-source';
import {
  InfoWindowContent,
  ProTasksMapMarkerService
} from '../pro-tasks-manager/marker-service/pro-tasks-map-marker.service';
import {PlacesService} from '../../../../core/component/places/places.service';
import {DeviceService} from '../../../../core/commons/device.service';
import {TranslateService} from '@ngx-translate/core';
import {AuthStorage} from '../../../../core/auth/auth-storage';
import {Permissions} from '../../../../core/auth/permissions';
import {Module} from '../../../../core/model/profile';
import {MonitorMarkerService} from '../../../../core/map/monitor-marker.service';
import {escapeForOnclickArg} from '../../../../core/map/map-popup-string.utils';
import {PopupConfigsForm} from '../../settings/global-config/popup-configs/popup-configs.form';
import {MapPOIEntry} from '../tasks-manager/tasks-poi/model/map-poi.entry';
import {StationPrice} from '../../../../core/model/station-price';
import {IntersectingMarkedRoadEntry} from '../pro-tasks-manager/form/model/intersecting-marked-road.entry';
import {PoiUnveroEntry} from '../pro-tasks-manager/form/model/unvero/poi-unvero.entry';
import {ProTaskManagerUtilsComponent} from '../pro-tasks-manager/utils/pro-task-manager-utils.component';
import {UnveroStationProductEntry} from '../pro-tasks-manager/form/model/unvero/unvero-station-product.entry';
import {v4 as UUID} from 'uuid';
import {Circle} from 'ol/geom';
import {Circle as CircleStyle, Stroke, Style} from 'ol/style';
import {fromLonLat} from 'ol/proj';
import Feature from 'ol/Feature';
import {TaskUtils} from '../tasks-manager/utils/task-utils';
import {UnveroUtils} from '../utils/unvero-utils';
import {ADBLUE_COLOR, DIESEL_COLOR} from '../../../../core/component/fueling-poi/fueling-poi-marker.service';
import {AlarmType} from "../../../../core/model/alarm-type";

@Injectable()
export class TrailerSwapMarkerService {

  public static readonly trailerSwapSelectZone = 'onclick="window.trailerSwapVehicleSelectMapComponentRef.'
    + 'zone.run(() => {window.trailerSwapVehicleSelectMapComponentRef.component.';
  public static readonly trailerSwapFormZone = 'onclick="window.trailerSwapFormsMapComponentRef.'
    + 'zone.run(() => {window.trailerSwapFormsMapComponentRef.component.';

  constructor(private translate: TranslateService,
              private placesService: PlacesService,
              private deviceService: DeviceService) {
  }

  /*------------------------------------------------Vehicle Info window-----------------------------------------------*/

  public getVehicleInfoWindowContent(vehicle: LatestVehicleEntry, ngZone: string): Observable<InfoWindowContent> {
    let sources = [PlaceSource.NOMINATIM_COORDINATES];
    let coordinates = `${vehicle.position.latitude.toFixed(6)},${vehicle.position.longitude.toFixed(6)}`;
    let showButtons = ngZone !== 'done';
    let activeNgZone = this.resolveActiveZone(ngZone);
    return new Observable(infoWindow => {
      let info: InfoWindowContent = {
        content: '',
        bubbleHeight: this.deviceService.isSafariBrowser() ? 74 : 64,
        bubbleWidth: 245
      };
      this.placesService.getPlaces(coordinates, sources).subscribe({
        next: (data) => {
          if (data.length > 1) {
            vehicle.address = data[1].name;
          }
          this.generateVehicleContent(vehicle, info, activeNgZone, showButtons);
          infoWindow.next(info);
        },
        error: () => {
          vehicle.address = '';
          this.generateVehicleContent(vehicle, info, activeNgZone, showButtons);
          infoWindow.next(info);
        }
      });
    });
  }

  public getMarkerInfoWindowContent(data: any): InfoWindowContent {
    let info: InfoWindowContent = {
      content: '',
      bubbleHeight: this.deviceService.isSafariBrowser() ? 74 : 64,
      bubbleWidth: 245
    };
    let destination = data.destination;
    let address = destination.address;
    let doneAt = data.doneAt;
    let routeInfo = data.routeInfo;
    let comment = data.comment;
    info.content += '<div class="popup-content"><div class="ol-popup-header">'
      + `<span class="map-popup__title">${this.translate.instant('tasks.type.' + data.type)}</span></div>`;
    info.content += '<div class="ol-info-data">';
    this.addInfoLine(info, '', `${destination.lat}, ${destination.lng}`, '');
    if (address) {
      this.addInfoLine(info, '', address, '');
    }
    if (doneAt) {
      this.addInfoLine(
        info,
        this.translate.instant('tasks.info.doneAt'),
        ': ' + doneAt,
        '');
    }
    if (routeInfo) {
      this.addInfoLine(
        info,
        this.translate.instant('proTasks.form.routeInfo'),
        ': ' + routeInfo,
        '');
    }
    if (comment) {
      this.addInfoLine(
        info,
        this.translate.instant('proTasks.form.comments'),
        ': ' + comment,
        '');
    }
    info.content += '</div>';
    return info;
  }

  public getPopupContent(
    latLng: any,
    address: string,
    uuid: any,
    poi?: MapPOIEntry,
    stationPrices?: StationPrice[],
    formZone?: string) {

    let info: InfoWindowContent = {
      content: '',
      bubbleHeight: this.deviceService.isSafariBrowser() ? 74 : 64,
      bubbleWidth: 245
    };
    let activeNgZone = this.resolveActiveZone(formZone);
    let header = poi != null
      ? `<div class="ol-popup-header"><span class="map-popup__title">${poi.name}</span></div>`
      : '';
    info.content += '<div class="popup-content">' + header;
    info.content += '<div class="ol-info-data">';
    info.content += '<div class="info-line"><span class="info-label">'
      + this.translate.instant('address-search.coordinates')
      + `</span><a target="_blank" href="http://www.google.com/maps/place/${latLng.lat},${latLng.lng}">`
      + `: ${latLng.lat}, ${latLng.lng}</a>`
      + '<a class="map-popup__link" target="_blank" '
      + `href="https://www.google.com/maps?q=&layer=c&cbll=${latLng.lat},${latLng.lng}">`
      + '<i class="ico-human"></i></a>'
      + '<a class="map-popup__link"'
      + 'title ="' + this.translate.instant('monitor.map.title.' + 'copy') + '"'
      + activeNgZone + `copyToClipboard('${latLng.lat},${latLng.lng}')` + '})">'
      + '<i class="ico-copy"></i></a></div></div>';
    info.bubbleHeight += 13;
    info.content += '<div class="ol-info-data">';
    info.content += '<div class="info-line"><span class="info-label">'
      + this.translate.instant('address-search.address')
      + `</span>: ${address}</div>`;
    if (address && address.length > 30) {
      info.bubbleHeight += 13;
    }
    if (poi != null) {
      this.addStationInfo(info, poi, stationPrices, this.translate)
    }
    info.content += '<div class="popup-buttons">';
    if ('vehicle-select' === formZone) {
      if (poi == null) {
        info.content += ProTasksMapMarkerService.getIconWithLink(
          `hideMarker('${uuid}')`,
          'ico-close',
          'close',
          this.translate,
          activeNgZone);
      }
    }
    if ('task-forms' === formZone) {
      if (poi != null) {
        if (MonitorMarkerService.is5StepsHotel(poi.poiGroup.name)) {
          info.content += ProTasksMapMarkerService.getIconWithLink(
            'openHotelForm(' + poi.id + ')',
            'ico-hotel',
            'fiveStepsHotel',
            this.translate,
            activeNgZone);
        }
      } else {
        info.content += ProTasksMapMarkerService.getIconWithLink(
          `hideMarker('${uuid}')`,
          'ico-close',
          'close',
          this.translate,
          activeNgZone);
      }
      info.content += ProTasksMapMarkerService.getIconWithLink(
        `addToRoute('${uuid}')`,
        'ico-location-arrow',
        'route',
        this.translate,
        activeNgZone);

      if (!AuthStorage.isForbiddenManageTasks()) {
        let poiInfo = poi != null ? poi : {latitude: latLng.lat, longitude: latLng.lng, externalId: null, name: null};
        let type = poi != null ? poi.poiGroup.type : null;
        this.addTaskInsertionButtons(info, poiInfo, this.translate, ProTaskManagerUtilsComponent.getPoiType(type), formZone)
      }
    }
    info.content += '</div></div>';
    return info;
  }

  private addStationInfo(
    info: InfoWindowContent,
    poi: any,
    stationPrices: StationPrice[],
    translate: TranslateService) {

    if (stationPrices) {
      this.showStationPrices(stationPrices, info);
    }
    if (poi.globalTankPOIPrices != null) {
      MonitorMarkerService.showGlobalTankPrices(poi.globalTankPOIPrices, info);
    }
    if (MonitorMarkerService.isAS24Station(poi.poiGroup.id)) {
      MonitorMarkerService.showGlobalTankPrices({dieselPrice: poi.fuelPrice, adBluePrice: poi.adbluePrice}, info);
    }
    this.addPoiGroupInfo(poi, info, translate);
  }

  private showStationPrices(stationPrices: StationPrice[], info: InfoWindowContent) {
    if (stationPrices && stationPrices.length > 0) {
      info.content += '<div class="fuel-prices">';
      //TODO: fix this crt garbage
      for (let i = 0; i < stationPrices.length; i++) {
        let product: any = stationPrices[i];
        if (product.price > 0) {
          let color = product.product === 'DIESEL' ? DIESEL_COLOR : ADBLUE_COLOR;
          info.content +=
            '<span class="fuel-type" style="background-color: ' + color + '">'
            + product.product + '</span>' +
            '<span class="info-label">' + product.price + ' ' + product.currency + '</span>';
          info.bubbleHeight += 13;
        }
      }
      info.content += '</div>';
    }
  }

  private addPoiGroupInfo(poi: MapPOIEntry, info: InfoWindowContent, translate: TranslateService) {
    if (poi.network) {
      this.addInfoLine(info, translate.instant('monitor.poi.network'), ': ' + poi.network, '');
    }
    info.bubbleHeight += 13;
    if (poi.poiGroup != null && poi.poiGroup.name !== null && poi.poiGroup.name !== 'CRT_FUEL_STATIONS') {
      this.addInfoLine(info, translate.instant('monitor.poi.group'), ': ' + poi.poiGroup.name, '');
    }
    if (poi.quantity) {
      this.addInfoLine(info, translate.instant('monitor.poi.quantity'), ': ' + poi.quantity, '');
    }
    if (poi.comment) {
      this.addInfoLine(info, translate.instant('monitor.poi.comment'), ': ' + poi.comment, '');
    }
    if (poi.link) {
      info.content += '<div class="info-line"><span class="info-label">'
        + translate.instant('monitor.poi.link')
        + '</span>: <a target="_blank" href="'
        + poi.link + '">' + poi.link + '</a></div>';
    }
  }

  private generateVehicleContent(vehicle: LatestVehicleEntry,
                                 info: InfoWindowContent,
                                 activeNgZone: string,
                                 showButtons: boolean): void {

    info.content += '<div class="popup-content"><div class="ol-popup-header">'
      + '<div class="vehicle-plate-number">' + vehicle.plateNumber
      + '</div><span>'
      + this.getCopyElement(vehicle.plateNumber, activeNgZone, 'copy-content')
      + '</span>';
    this.addDriver(vehicle, info, activeNgZone);
    info.content += '</div>';
    this.addMainContent(vehicle, info, activeNgZone);
    if (showButtons) {
      this.addButtons(vehicle, activeNgZone, info);
    }
  }

  private getCopyElement(text: string, activeNgZone: string, title: string = 'copy') {
    text = escapeForOnclickArg(text);
    return '<a class="map-popup__link" '
      + 'title ="' + this.translate.instant('monitor.map.title.' + title) + '" '
      + activeNgZone
      + `copyTextToClipboard('${text}')` + '})">'
      + '<i class="ico-copy"></i></a>';
  }

  private addDriver(vehicle: LatestVehicleEntry, info: InfoWindowContent, activeNgZone: string): void {
    if (vehicle.driver) {
      info.content += '<div class="driver-text"> '
        + vehicle.driver.value
        + this.getCopyElement(vehicle.driver.value, activeNgZone, 'copy-content')
        + '</div>';
    }
  }

  // todo refactor most of the methods to utils for better reusability
  private addMainContent(vehicle: LatestVehicleEntry, info: InfoWindowContent, activeNgZone: string): void {
    let popupConfigs = AuthStorage.getPopupConfigs();
    info.content += '<div class="ol-info-data">';
    if (popupConfigs.plateNumber) {
      info.content += '<div class="info-line">'
        + '<span class="info-label">'
        + this.translate.instant('vehicles.list.licensePlate')
        + '</span>'
        + '<span class="info-line" id="info-plate-number">' + ': ' + vehicle.plateNumber + ' ' + '' + '</span>'
        + '</div>';
      info.bubbleHeight += 13;
    }
    if (vehicle.address && popupConfigs.address) {
      ProTasksMapMarkerService.createInfoLine(info, this.translate.instant('monitor.vehicles.address'), vehicle.address, '');
    }
    if (vehicle.position.latitude && vehicle.position.longitude && popupConfigs.position) {
      info.content += '<div class="info-line"><span class="info-label">'
        + this.translate.instant('vehicles.dvr.track.infoWindow.location')
        + '</span>: <a target="_blank" href="http://www.google.com/maps/place/'
        + vehicle.position.latitude + ',' + vehicle.position.longitude + '" class="map-popup__link">'
        + vehicle.position.latitude + ', ' + vehicle.position.longitude + '</a>'
        + '<a class="map-popup__link" target="_blank" href="https://www.google.com/maps?q=&layer=c&cbll='
        + vehicle.position.latitude + ',' + vehicle.position.longitude + '">'
        + '<i class="ico-human"></i></a>'
        + this.getCopyElement(vehicle.position.latitude + ',' + vehicle.position.longitude, activeNgZone, 'copy')
        + '</div>';
      info.bubbleHeight += 13;
    }

    if (vehicle.speed && popupConfigs.speed) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.speed'),
        vehicle.speed,
        this.translate.instant('unit.kmph'));
    }
    if (vehicle.fuel && Permissions.hasModule(Module.HEAVY_TRACK) && popupConfigs.fuel) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.fuel'),
        vehicle.fuel,
        this.translate.instant('unit.l'));
    }
    if (vehicle.float1Liters && Permissions.hasModule(Module.HEAVY_TRACK) && popupConfigs.fuel) {
      let liters: string | number;
      if (vehicle.float2Liters) {
        let sum: number = vehicle.float1Liters + vehicle.float2Liters;
        liters = '(' + vehicle.float1Liters + '+' + vehicle.float2Liters + ')=' + sum;
      } else {
        liters = vehicle.float1Liters;
      }
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.floatLiters'),
        liters,
        this.translate.instant('unit.l'));
    }

    if (vehicle.axleWeight && Permissions.hasModule(Module.HEAVY_TRACK) && popupConfigs.axleWeight) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.axleWeight'),
        vehicle.axleWeight,
        this.translate.instant('unit.kg'));
    }
    if (vehicle.axleWeight2 && Permissions.hasModule(Module.HEAVY_TRACK) && popupConfigs.axleWeight2) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.axleWeight2'),
        vehicle.axleWeight2,
        this.translate.instant('unit.kg'));
    }
    if (vehicle.grossCombinationVehicleWeight != null
      && Permissions.hasModule(Module.HEAVY_TRACK)
      && popupConfigs.grossCombinationVehicleWeight) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.grossCombinationVehicleWeight'),
        vehicle.grossCombinationVehicleWeight,
        this.translate.instant('unit.kg'));
    }
    if (vehicle.tabTime && popupConfigs.tabTime) {
      MonitorMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.tab-gps-update'),
        vehicle.tabTime,
        '');
    }
    if (vehicle.time && popupConfigs.time) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.gps-update'),
        vehicle.time,
        '');
    }
    if (vehicle.distance && Permissions.hasModule(Module.HEAVY_TRACK) && popupConfigs.distance) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.distance'),
        vehicle.distance,
        this.translate.instant('unit.km'));
    }
    if (vehicle.gpsOdometer && Permissions.hasModule(Module.HEAVY_TRACK) && popupConfigs.distance) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.gpsOdometer'),
        vehicle.gpsOdometer,
        this.translate.instant('unit.km'));
    }
    if (vehicle.distanceToday && Permissions.hasModule(Module.HEAVY_TRACK) && popupConfigs.distanceToday) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.distanceToday'),
        vehicle.distanceToday,
        this.translate.instant('unit.km'));
    }
    if (vehicle.taskDistance
      && Permissions.hasModule(Module.HEAVY_TRACK)
      && vehicle.taskCount > 0
      && popupConfigs.taskDistance) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.taskDistance'),
        (vehicle.taskDistance / 1000).toFixed(1),
        this.translate.instant('unit.km'));
    }
    if (vehicle.externalPowerV && Permissions.hasModule(Module.HEAVY_TRACK) && popupConfigs.externalPowerV) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.externalPowerV'),
        vehicle.externalPowerV,
        this.translate.instant('unit.mv'));
    }
    if (vehicle.engineHours && Permissions.hasModule(Module.HEAVY_TRACK) && popupConfigs.engineHours) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.engineHours'),
        vehicle.engineHours,
        this.translate.instant('unit.h'));
    }
    if (vehicle.connectedTrailer != null && Permissions.hasModule(Module.HEAVY_TRACK)) {
      this.addConnectedTrailerData(vehicle, popupConfigs, info);
    }
    this.addVehicleTemperatureData(vehicle, popupConfigs, info);
    if (vehicle.driverSimNo && popupConfigs.driverPhoneNumber) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.driverSimNo'),
        vehicle.driverSimNo,
        '');
    }
    if (vehicle.vehiclePhoneNumber && popupConfigs.vehiclePhoneNumber) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.vehiclePhoneNumber'),
        vehicle.vehiclePhoneNumber,
        '');
    }
    if (vehicle.securityMode) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.LOOP_OFF'),
        vehicle.warnings.alarms.includes(AlarmType.LOOP_OFF) ? 'OFF' : 'ON',
        '');
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.DOORS_OFF'),
        vehicle.warnings.alarms.includes(AlarmType.DOORS_OFF) ? 'OFF' : 'ON',
        '');
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.LOOP_ALARM'),
        vehicle.warnings.alarms.includes(AlarmType.LOOP_ALARM) ? 'ON' : 'OFF',
        '');
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.DOORS_ALARM'),
        vehicle.warnings.alarms.includes(AlarmType.DOORS_ALARM) ? 'ON' : 'OFF',
        '');
    }
    info.content += '</div>';
  }

  private addVehicleTemperatureData(vehicle: LatestVehicleEntry, popupSettings: PopupConfigsForm, info: InfoWindowContent) {
    // ---------------------------- First compartment -----------------------------------
    if (vehicle.setFreezerTemperature != null && popupSettings.setFreezerTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.setFreezerTemperature'),
        vehicle.setFreezerTemperature,
        this.translate.instant('unit.t'));
    }
    const connectedTrailer = vehicle.connectedTrailer;
    if (vehicle.supplyFreezerTemperature != null && connectedTrailer == null && popupSettings.supplyFreezerTemperature
      || (vehicle.supplyFreezerTemperature != null &&
        connectedTrailer != null
        && connectedTrailer.supplyFreezerTemperature == null)) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.supplyFreezerTemperature'),
        vehicle.supplyFreezerTemperature,
        this.translate.instant('unit.t'));
    }
    if (vehicle.actualFreezerTemperature != null && popupSettings.actualFreezerTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.actualFreezerTemperature'),
        vehicle.actualFreezerTemperature,
        this.translate.instant('unit.t'));
    }
    if (vehicle.freezerEngineOn != null && popupSettings.freezerEngineOn) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerEngineOn'),
        vehicle.freezerEngineOn
          ? this.translate.instant('monitor.vehicles.ON')
          : this.translate.instant('monitor.vehicles.OFF'),
        '');
    }
    if (vehicle.freezerMode != null && popupSettings.freezerMode) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerMode'),
        this.translate.instant('monitor.vehicles.freezerModes.' + vehicle.freezerMode),
        '');
    }
    // ---------------------------- Second compartment -----------------------------------
    if (vehicle.secondCompartmentSetPoint != null && popupSettings.secondCompartmentSetPoint) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.secondCompartmentSetPoint'),
        vehicle.secondCompartmentSetPoint,
        this.translate.instant('unit.t'));
    }
    if (vehicle.secondCompartmentSupplyTemperature != null && popupSettings.secondCompartmentSupplyTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.secondCompartmentSupplyTemperature'),
        vehicle.secondCompartmentSupplyTemperature,
        this.translate.instant('unit.t'));
    }
    if (vehicle.secondCompartmentTemperature != null && popupSettings.secondCompartmentTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.secondCompartmentTemperature'),
        vehicle.secondCompartmentTemperature,
        this.translate.instant('unit.t'));
    }
    if (vehicle.secondCompartmentFreezerMode != null && popupSettings.secondCompartmentFreezerMode) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.secondCompartmentFreezerMode'),
        vehicle.secondCompartmentFreezerMode,
        '');
    }
    if (vehicle.secondCompartmentIsOn != null && popupSettings.secondCompartmentIsOn) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.secondCompartmentIsOn'),
        vehicle.secondCompartmentIsOn
          ? this.translate.instant('monitor.vehicles.ON')
          : this.translate.instant('monitor.vehicles.OFF'),
        '');
    }
    // ---------------------------- Sensor temps ----------------------------------------
    if (vehicle.firstSensorTemperature != null && popupSettings.firstSensorTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.firstSensorTemperature'),
        vehicle.firstSensorTemperature,
        this.translate.instant('unit.t'));
    }
    if (vehicle.secondSensorTemperature != null && popupSettings.secondSensorTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.secondSensorTemperature'),
        vehicle.secondSensorTemperature,
        this.translate.instant('unit.t'));
    }
    if (vehicle.thirdSensorTemperature != null && popupSettings.thirdSensorTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.thirdSensorTemperature'),
        vehicle.thirdSensorTemperature,
        this.translate.instant('unit.t'));
    }
    if (vehicle.fourthSensorTemperature != null && popupSettings.fourthSensorTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.fourthSensorTemperature'),
        vehicle.fourthSensorTemperature,
        this.translate.instant('unit.t'));
    }
    // ---------------------------- Other -----------------------------------------------
    if (vehicle.ambientTemperature != null && popupSettings.ambientTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.ambientTemperature'),
        vehicle.ambientTemperature,
        this.translate.instant('unit.t'));
    }
    if (vehicle.freezerPowerMode != null && popupSettings.freezerPowerMode) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerPowerMode'),
        this.translate.instant('monitor.vehicles.freezerPowerModes.' + vehicle.freezerPowerMode),
        '');
    }
    if (vehicle.freezerRunMode != null && popupSettings.freezerRunMode) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerRunMode'),
        this.translate.instant('monitor.vehicles.freezerRunModes.' + vehicle.freezerRunMode),
        '');
    }
    if (vehicle.freezerDieselHours != null && popupSettings.freezerDieselHours) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerDieselHours'),
        vehicle.freezerDieselHours,
        this.translate.instant('unit.h'));
    }
    if (vehicle.freezerElectricHours != null && popupSettings.freezerElectricHours) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerElectricHours'),
        vehicle.freezerElectricHours,
        this.translate.instant('unit.h'));
    }
    if (vehicle.freezerStandbyHours != null && popupSettings.freezerStandbyHours) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerStandbyHours'),
        vehicle.freezerStandbyHours,
        this.translate.instant('unit.h'));
    }
    if (vehicle.engineTemperature != null && popupSettings.engineTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.engineTemperature'),
        vehicle.engineTemperature,
        this.translate.instant('unit.t'));
    }
  }

  private addConnectedTrailerData(vehicle: LatestVehicleEntry, popupSettings: PopupConfigsForm, info: InfoWindowContent) {
    // ---------------------------- First compartment -----------------------------------
    const trailer = vehicle.connectedTrailer;
    if (trailer.setFreezerTemperature != null
      && vehicle.setFreezerTemperature == null && popupSettings.setFreezerTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.setFreezerTemperature'),
        trailer.setFreezerTemperature,
        this.translate.instant('unit.t'));
    }
    if (trailer.supplyFreezerTemperature != null
      && popupSettings.supplyFreezerTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.supplyFreezerTemperature'),
        trailer.supplyFreezerTemperature,
        this.translate.instant('unit.t'));
    }
    if (trailer.actualFreezerTemperature != null
      && vehicle.actualFreezerTemperature == null && popupSettings.actualFreezerTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.actualFreezerTemperature'),
        trailer.actualFreezerTemperature,
        this.translate.instant('unit.t'));
    }
    if (trailer.freezerMode != null && popupSettings.freezerMode) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerMode'),
        this.translate.instant('monitor.vehicles.freezerModes.' + trailer.freezerMode),
        '');
    }
    if (trailer.freezerEngineOn != null
      && vehicle.freezerEngineOn == null && popupSettings.freezerEngineOn) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerEngineOn'),
        trailer.freezerEngineOn
          ? this.translate.instant('monitor.vehicles.ON')
          : this.translate.instant('monitor.vehicles.OFF'),
        '');
    }
    // ---------------------------- Second compartment -----------------------------------
    if (trailer.secondCompartmentSetPoint != null
      && vehicle.secondCompartmentSetPoint == null && popupSettings.secondCompartmentSetPoint) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.secondCompartmentSetPoint'),
        trailer.secondCompartmentSetPoint,
        this.translate.instant('unit.t'));
    }
    if (trailer.secondCompartmentSupplyTemperature != null
      && vehicle.secondCompartmentSupplyTemperature == null && popupSettings.secondCompartmentSupplyTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.secondCompartmentSupplyTemperature'),
        trailer.secondCompartmentSupplyTemperature,
        this.translate.instant('unit.t'));
    }
    if (trailer.secondCompartmentTemperature != null
      && vehicle.secondCompartmentTemperature == null && popupSettings.secondCompartmentTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.secondCompartmentTemperature'),
        trailer.secondCompartmentTemperature,
        this.translate.instant('unit.t'));
    }
    if (trailer.secondCompartmentFreezerMode != null
      && vehicle.secondCompartmentFreezerMode == null && popupSettings.secondCompartmentFreezerMode) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.secondCompartmentFreezerMode'),
        trailer.secondCompartmentFreezerMode,
        this.translate.instant('unit.t'));
    }
    if (trailer.secondCompartmentIsOn != null
      && vehicle.secondCompartmentIsOn == null && popupSettings.secondCompartmentIsOn) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.secondCompartmentIsOn'),
        trailer.secondCompartmentIsOn
          ? this.translate.instant('monitor.vehicles.ON')
          : this.translate.instant('monitor.vehicles.OFF'),
        '');
    }
    // ---------------------------- Sensor temps ----------------------------------------
    if (trailer.firstSensorTemperature
      && vehicle.firstSensorTemperature == null && popupSettings.firstSensorTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.firstSensorTemperature'),
        trailer.firstSensorTemperature,
        this.translate.instant('unit.t'));
    }
    if (trailer.secondSensorTemperature
      && vehicle.secondSensorTemperature == null && popupSettings.secondSensorTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.secondSensorTemperature'),
        trailer.secondSensorTemperature,
        this.translate.instant('unit.t'));
    }
    if (trailer.thirdSensorTemperature
      && vehicle.thirdSensorTemperature == null && popupSettings.thirdSensorTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.thirdSensorTemperature'),
        trailer.thirdSensorTemperature,
        this.translate.instant('unit.t'));
    }
    if (trailer.fourthSensorTemperature
      && vehicle.fourthSensorTemperature == null && popupSettings.fourthSensorTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.fourthSensorTemperature'),
        trailer.fourthSensorTemperature,
        this.translate.instant('unit.t'));
    }
    // ---------------------------- Other -----------------------------------------------
    if (trailer.ambientTemperature != null && popupSettings.ambientTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.ambientTemperature'),
        trailer.ambientTemperature,
        this.translate.instant('unit.t'));
    }
    if (trailer.freezerPowerMode != null && popupSettings.freezerPowerMode) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerPowerMode'),
        this.translate.instant('monitor.vehicles.freezerPowerModes.' + trailer.freezerPowerMode),
        '');
    }
    if (trailer.freezerRunMode != null && popupSettings.freezerRunMode) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerRunMode'),
        this.translate.instant('monitor.vehicles.freezerRunModes.' + trailer.freezerRunMode),
        '');
    }
    if (trailer.freezerDieselHours != null && popupSettings.freezerDieselHours) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerDieselHours'),
        trailer.freezerDieselHours,
        this.translate.instant('unit.h'));
    }
    if (trailer.freezerElectricHours != null && popupSettings.freezerElectricHours) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerElectricHours'),
        trailer.freezerElectricHours,
        this.translate.instant('unit.h'));
    }
    if (trailer.freezerStandbyHours != null && popupSettings.freezerStandbyHours) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerStandbyHours'),
        trailer.freezerStandbyHours,
        this.translate.instant('unit.h'));
    }
  }

  private addButtons(vehicle: LatestVehicleEntry, ngZone: string, info: InfoWindowContent) {
    let plateNumber = escapeForOnclickArg(vehicle.plateNumber);
    info.content += '<div class="popup-buttons">';
    /*------------------------------------------Chat---------------------------------------*/
    if (vehicle.hasTab && AuthStorage.isLoggedClientEmployee()) {
      let driver = escapeForOnclickArg(vehicle.driver ? vehicle.driver.value : null);
      info.content += ProTasksMapMarkerService.getIconWithLink('openChat(' + vehicle.id + ', \''
        + plateNumber + '\', \'' + driver + '\', \''
        + vehicle.warnings.tabOffline + '\', \''
        + vehicle.driverLanguage + '\')',
        'ico-comment',
        'chat',
        this.translate,
        ngZone);
      info.bubbleWidth += 5
    }
    /*------------------------------------------Tacho graph / table---------------------------------------*/
    if (vehicle.driver && vehicle.hasTacho && Permissions.hasModule(Module.HEAVY_TRACK)) {
      let secondDriverText = escapeForOnclickArg(vehicle.secondDriver ? vehicle.secondDriver.text : null);
      let secondDriverValue = escapeForOnclickArg(vehicle.secondDriver ? vehicle.secondDriver.value : null);
      let driverText = escapeForOnclickArg(vehicle.driver.text);
      let driverValue = escapeForOnclickArg(vehicle.driver.value);
      info.content += ProTasksMapMarkerService.getIconWithLink('showHideTacho(\'' + plateNumber + '\', '
        + vehicle.id + ', '
        + '\'CHART\', \'' + driverText + '\', \'' + driverValue + '\', \''
        + secondDriverText + '\', \'' + secondDriverValue + '\')',
        'ico-chart-bar',
        'charts',
        this.translate,
        ngZone);
      info.content += ProTasksMapMarkerService.getIconWithLink('showHideTacho(\'' + plateNumber + '\', '
        + vehicle.id + ', '
        + '\'TABLE\', \'' + driverText + '\', \'' + driverValue + '\', \''
        + secondDriverText + '\', \'' + secondDriverValue + '\')',
        'ico-table',
        'tables',
        this.translate,
        ngZone);
      info.bubbleWidth += 5;
    }
  }

  private addInfoLine(info: InfoWindowContent, name: string, value: any, units: string) {
    info.content += '<div class="info-line"><span class="info-label">'
      + name + '</span> ' + value + ' ' + units + '</div>';
    info.bubbleHeight += 13;
  }

  public getMarkedRoadInfoWindowContent(data: IntersectingMarkedRoadEntry): InfoWindowContent {
    let info: InfoWindowContent = {
      content: '',
      bubbleHeight: this.deviceService.isSafariBrowser() ? 74 : 64,
      bubbleWidth: 245
    };
    info.content += '<div class="popup-content"><div class="ol-popup-header">'
      + `<span class="map-popup__title">${this.translate.instant('proTasks.map.marked-road')}</span></div>`;
    info.content += '<div class="ol-info-data">';
    info.content += `<div style="text-align: center" class="info-line">${data.roadGroupTitle}</div>`;
    info.content += `<div style="text-align: center" class="info-line">${data.description}</div>`;
    return info;
  }

  public getStationsInfoWindowContent(poi: any): InfoWindowContent {
    let info: InfoWindowContent = {
      content: '',
      bubbleHeight: this.deviceService.isSafariBrowser() ? 74 : 64,
      bubbleWidth: 245
    };
    this.generateStationsContent(info, poi);
    return info;
  }

  private generateStationsContent(info: InfoWindowContent, poi: any) {
    let lat = +poi.latitude;
    let lng = +poi.longitude;
    info.content += '<div class="popup-content"><div class="ol-popup-header">'
      + `<span class="map-popup__title">${poi.name}</span></div>`;
    info.content += '<div class="ol-info-data">';
    info.content += '<div class="info-line"><span class="info-label">'
      + this.translate.instant('address-search.address')
      + `</span>: ${poi.address}</div>`;
    if (poi.address && poi.address.length > 30) {
      info.bubbleHeight += 13;
    }
    info.content += '<div class="info-line"><span class="info-label">'
      + this.translate.instant('address-search.coordinates')
      + `</span><a target="_blank" href="http://www.google.com/maps/place/${lat},${lng}">`
      + `: ${lat.toFixed(6)}, ${lng.toFixed(6)}</a>`
      + `<a class="map-popup__link" target="_blank"`
      + `href="https://www.google.com/maps?q=&layer=c&cbll=${lat},${lng}">`
      + '<i class="ico-human"></i></a>'
      + '<a class="map-popup__link"'
      + 'title ="' + this.translate.instant('monitor.map.title.' + 'copy') + '"'
      + TrailerSwapMarkerService.trailerSwapFormZone + `copyToClipboard('${lat},${lng}')` + '})">'
      + '<i class="ico-copy"></i></a></div></div>';
    info.bubbleHeight += 13;
    ProTasksMapMarkerService.addStationInfo(info, poi, this.translate);
    if (poi.parkingDetails) {
      MonitorMarkerService.addPoiParkingDetails(poi.parkingDetails, info, this.translate);
    }
    info.content += '<div class="popup-buttons">';
    this.addTaskInsertionButtons(info, poi, this.translate, ProTaskManagerUtilsComponent.getPoiType(poi.poiGroup.type));
    info.content += '</div></div>';
  }

  private addTaskInsertionButtons(
    info: InfoWindowContent,
    poi: any,
    translate: TranslateService,
    poiType: string,
    managerViewType?: string) {

    info.content += this.taskInsertActionLink(poi, translate, 'autoImport', false, poiType, managerViewType, false);
    info.content += this.taskInsertActionLink(poi, translate, 'importToLastPosition', true, poiType, managerViewType, false);
    info.content += this.taskInsertActionLink(poi, translate, 'importSelected', false, poiType, managerViewType, true);
  }

  private taskInsertActionLink(
    poi: any,
    translate: TranslateService,
    tooltip: string,
    autoInsert: boolean,
    poiType: string,
    managerViewType?: string,
    importSelected?: boolean) {

    const comment = escapeForOnclickArg(poi.comment);
    const name = escapeForOnclickArg(poi.name);
    const contactlessType = TaskUtils.resolveContactlessType(poi);
    let refZoneRun = this.resolveActiveZone(managerViewType);

    return '<a class="icon-action"'
      + refZoneRun
      // eslint-disable-next-line max-len
      + `insertProTask(${poi.latitude}, ${poi.longitude}, ${autoInsert}, '${poi.externalId}', '${poi.id}', '${comment}', '${poiType}', '${contactlessType}', '${poi.emails}', '${name}', ${importSelected})})">`
      + `${translate.instant('proTasks.map.' + tooltip)}`
      + '</a>';
  }

  public getUnveroInfoWindowContent(station: PoiUnveroEntry, comment: string): InfoWindowContent {
    let info: InfoWindowContent = {
      content: '',
      bubbleHeight: this.deviceService.isSafariBrowser() ? 74 : 64,
      bubbleWidth: 245
    };
    this.generateUnveroContent(info, station, comment);
    return info;
  }

  private generateUnveroContent(info: InfoWindowContent, station: PoiUnveroEntry, comment: string): void {
    let lat = station.lat;
    let lng = station.lng;
    info.content += '<div class="popup-content"><div class="ol-popup-header">'
      + `<span class="map-popup__title">${station.partnerName}</span></div>`;
    info.content += '<div class="ol-info-data">';
    info.content += '<div class="info-line"><span class="info-label">'
      + this.translate.instant('tasks.map.unveroPopup.brand')
      + `</span>: ${station.brand}</div>`;
    info.content += '<div class="info-line"><span class="info-label">'
      + this.translate.instant('tasks.map.unveroPopup.stationId')
      + `</span>: ${station.gasStationId}</div>`;
    info.content += '<div class="info-line"><span class="info-label">'
      + this.translate.instant('address-search.coordinates')
      + `</span><a target="_blank" href="http://www.google.com/maps/place/${lat},${lng}">`
      + `: ${lat.toFixed(6)}, ${lng.toFixed(6)}</a>`
      + `<a class="map-popup__link" target="_blank"`
      + `href="https://www.google.com/maps?q=&layer=c&cbll=${lat},${lng}">`
      + '<i class="ico-human"></i></a>'
      + '<a class="map-popup__link"'
      + 'title ="' + this.translate.instant('monitor.map.title.' + 'copy') + '"'
      + TrailerSwapMarkerService.trailerSwapFormZone + `copyToClipboard('${lat},${lng}')` + '})">'
      + '<i class="ico-copy"></i></a></div></div>';
    info.content += '<div class="info-line"><span class="info-label">'
      + this.translate.instant('address-search.address')
      + `</span>: ${station.address}</div>`;
    info.content += '<div class="info-line"><span class="info-label">'
      + this.translate.instant('tasks.map.unveroPopup.hasParking')
      + `</span>: <i class="${station.hasParking ? 'ico-check' : 'ico-close'}"
      + style="color: ${station.hasParking ? '#00a65a' : '#b63019'}"></i></div>`;
    info.content += '<div class="info-line"><span class="info-label">'
      + this.translate.instant('tasks.map.unveroPopup.hasShop')
      + `</span>: <i class="${station.hasShop ? 'ico-check' : 'ico-close'}"
      + style="color: ${station.hasShop ? '#00a65a' : '#b63019'}"></i></div>`;
    if (station.products && station.products.length > 0) {
      this.insertUnveroProducts(info, station.products);
    }
    if (station.gasStationComment != null && station.gasStationComment !== '') {
      this.addInfoLine(info, this.translate.instant('monitor.poi.comment'), ': ' + station.gasStationComment, '');
    }
    info.content += '<div class="popup-buttons">';
    this.addUnveroTaskInsertionButtons(info, station, this.translate, comment);
    info.content += '</div></div>';
  }

  private addUnveroTaskInsertionButtons(
    info: InfoWindowContent,
    station: PoiUnveroEntry,
    translate: TranslateService,
    comment: string) {

    comment = escapeForOnclickArg(comment);
    info.content += this.unveroTaskInsertActionLink(station, 'autoImport', translate, false, comment, false);
    info.content += this.unveroTaskInsertActionLink(station, 'importToLastPosition', translate, true, comment, false);
    info.content += this.unveroTaskInsertActionLink(station, 'importSelected', translate, false, comment, true);
  }

  private insertUnveroProducts(info: InfoWindowContent, products: UnveroStationProductEntry[]) {
    for (let i = 0; i < products.length; i++) {
      let product: any = products[i];
      if (product.price && product.price > 0) {
        let color = product.name === 'DIESEL' ? DIESEL_COLOR : ADBLUE_COLOR;
        info.content += '<div class="fuel-prices">';
        info.content +=
          '<span class="fuel-type" style="background-color: ' + color + '">'
          + product.name + '</span>'
          + '<span class="info-label">' + product.price + ' ' + product.currency + '/L' + '</span>';
        if (product.totalCost && product.totalCost > 0) {
          info.content += '<span class="info-label">'
            + '. '
            + this.translate.instant('tasks.map.unveroPopup.totalCost')
            + ': '
            + product.totalCost + product.currency + '</span>';
        }
        info.content += '</div>';
      }
    }
  }

  private unveroTaskInsertActionLink(
    station: PoiUnveroEntry,
    tooltip: string,
    translate: TranslateService,
    autoInsert: boolean,
    comment: string,
    importSelected?: boolean) {

    const contactlessType = UnveroUtils.resolveUnveroContactless(station);
    const externalId =contactlessType != null ? station.externalPoiId : null;
    return '<a class="icon-action"'
      + TrailerSwapMarkerService.trailerSwapFormZone
      + `insertProTask(
        ${station.lat},
        ${station.lng},
        ${autoInsert},
        '${externalId}',
        '${null}',
        '${comment}',
        'FUELLING',
        ${contactlessType},
        '${null}',
        '${null}',
        ${importSelected},
        '${station.gasStationId}',)})">`
      + `${translate.instant('proTasks.map.' + tooltip)}`
      + '</a>';
  }

  public getTrafficInfoWindowContent(data: any): InfoWindowContent {
    let info: InfoWindowContent = {
      content: '',
      bubbleHeight: this.deviceService.isSafariBrowser() ? 74 : 64,
      bubbleWidth: 245
    };
    info.content += '<div class="popup-content"><div class="ol-popup-header">'
      + `<span class="map-popup__title">${this.translate.instant('proTasks.map.' + data.type)}</span></div>`;
    info.content += '<div class="ol-info-data">';
    info.content += `<div style="text-align: center" class="info-line">${data.text}</div>`;
    if (data.from != null && data.from !== '') {
      info.content += '<div style="text-align: center" class="info-line">'
        + this.translate.instant('proTasks.map.from') + ' ' + `${data.from}`
        + ' '
        + this.translate.instant('proTasks.map.till') + ' ' + `${data.till}`
        + '</div>';
    }
    return info;
  }

  private resolveActiveZone(zoneName: string): string {
    switch (zoneName) {
      case 'task-forms':
        return TrailerSwapMarkerService.trailerSwapFormZone;
      case 'vehicle-select':
      default:
        return TrailerSwapMarkerService.trailerSwapSelectZone;
    }
  }

  public drawSwapPointCircle(coords: any): Feature {
    const circleFeature = new Feature({
      name: UUID(),
      geometry: new Circle(fromLonLat([coords.lng, coords.lat]), 50000),
      type: 'swap-area',
    });
    const stroke = new Stroke({
      color: 'rgba(46,143,215,0.7)',
      width: 4,
    });
    const style = new Style({
      stroke: stroke,
      image: new CircleStyle({
        radius: 5,
        fill: null,
        stroke: stroke,
      }),
    });
    circleFeature.setStyle(style);
    return circleFeature;
  }

}
